/**
 * The work area inside a web address, with its namespace (I-0656, AC-M19; R-5, F-0250).
 *
 * Jay, 2026-09-24: *"work area names are only unique in a namespace."* Jay, 2026-09-26: *"I think a /
 * is better between handle and workarea"* and *"first level is always the handle"*.
 *
 * ⭐ **A route reference is the namespace path and the slug, joined by `/`:** `alice/tracker`, and
 * for a subgroup `acme/platform/api`. It is the reference the shared resolver reads, and
 * `workAreaRoutePath` turns it into the address `/acme/platform/api/-/items`. The interim form,
 * `acme~platform~api` under `/p/`, is read only by `legacyAddress.ts`.
 */
import { namespaceUrlSegment } from '../namespaceKey.js';
import { workAreaDisplayName } from '../work-area-seed-names.js';
import {
  resolveWorkAreaReference,
  type IWorkAreaAddressHold,
  type IWorkAreaCandidate,
  type WorkAreaResolutionType,
} from './workAreaReference.js';

/**
 * What a route reference is built from: the slug, and the namespace's full path and key when known.
 */
export interface IWorkAreaRouteSubject {
  readonly slug: string;
  readonly namespace?: string | null;
  /** The namespace's immutable key (I-0681). Which of the two a route shows is D-0076. */
  readonly namespaceKey?: string | null;
}

/**
 * The route reference for a work area: `alice/tracker`, or the bare slug when the namespace is not
 * known (a server that predates namespaces). A bare reference is resolved by AC-M18, never by first
 * match, and `workAreaRoutePath` sends it to the address that resolves it.
 *
 * ⭐ The namespace half comes from `namespaceUrlSegment` (AC-K10), the one place that decides whether
 * an address shows the handle or the key (D-0076). `<key>/tracker` resolves either way.
 */
export function workAreaRouteRef(workArea: IWorkAreaRouteSubject): string {
  const namespace = namespaceUrlSegment({
    fullPath: workArea.namespace,
    key: workArea.namespaceKey,
  });
  if (!namespace) return workArea.slug;
  return `${namespace}/${workArea.slug}`;
}

/**
 * The key-form route reference, `<key>/<slug>`: the one every link FEATit produces for somebody to keep
 * uses (D-0076, AC-K12). The key is the namespace's internal id; this form resolves for as long as
 * the namespace exists, whatever its handle becomes. Without a known key it falls back to the
 * displayed form, which is what an older server leaves possible.
 */
export function workAreaPermalinkRef(workArea: IWorkAreaRouteSubject): string {
  const key = workArea.namespaceKey?.trim();
  return key ? `${key}/${workArea.slug}` : workAreaRouteRef(workArea);
}

/**
 * Resolve a route reference against the work areas a reader can see, by AC-M18's rules — and, since
 * I-1052, against the addresses held for a work area that was renamed or moved out from under it.
 */
export function resolveWorkAreaRouteRef(
  available: readonly IWorkAreaCandidate[],
  routeRef: string,
  holds: readonly IWorkAreaAddressHold[] = [],
): WorkAreaResolutionType {
  return resolveWorkAreaReference(available, routeRef, holds);
}

/** What a qualified label is built from. */
export interface IWorkAreaLabelSubject {
  readonly name: string;
  /** F-0737 PW-R3: set while the name is the product's, drawn in the reader's language. */
  readonly nameSeedId?: string | null;
  /** The namespace's display name (I-0657). Preferred over the path when present. */
  readonly namespaceName?: string | null;
  /** The namespace's full path, used when no display name is known. */
  readonly namespace?: string | null;
}

/**
 * `Acme / Tracker`: how a person sees a work area anywhere it is named on its own (I-0655,
 * AC-M14). Two work areas called Tracker in two namespaces never read alike.
 */
export function workAreaQualifiedLabel(workArea: IWorkAreaLabelSubject): string {
  const namespace = workAreaNamespaceLabel(workArea);
  const name = workAreaDisplayName(workArea);
  return namespace ? `${namespace} / ${name}` : name;
}

/**
 * The namespace part of `workAreaQualifiedLabel`, for a line that draws the two parts apart (I-1610:
 * on a phone the namespace is shortened before the work area's name). `null` where none is known.
 */
export function workAreaNamespaceLabel(workArea: IWorkAreaLabelSubject): string | null {
  return workArea.namespaceName?.trim() || workArea.namespace?.trim() || null;
}

/** A work area among others on one list, for `workAreaNamespaceAmong`. */
export interface IWorkAreaListedSubject extends IWorkAreaLabelSubject {
  readonly id: string;
  /**
   * Tells two namespaces apart where the caller holds no path: a session's work area carries the
   * namespace's id and name only (I-1514). Compared before `namespace`.
   */
  readonly namespaceId?: string | null;
}

const listedNameKey = (name: string) => name.trim().toLowerCase();
const namespaceIdentity = (workArea: IWorkAreaListedSubject) =>
  workArea.namespaceId ?? workArea.namespace ?? null;

/**
 * The namespace a list names before a work area's name, where the name alone is ambiguous on that
 * list: another listed work area has the same name (trimmed, in any case) in another namespace.
 * `null` where the name is the list's only one of it, or where the namespace is not known.
 *
 * ⭐ **One rule for every list that names work areas** — the scope line (AC-19.14) and the Agents
 * view's headings, rows and details (AC-TB17). A unique name stays bare, because a namespace on every
 * heading is noise the reader has to read past; only a shared one needs telling apart.
 */
export function workAreaNamespaceAmong(
  workArea: IWorkAreaListedSubject,
  listed: readonly IWorkAreaListedSubject[],
): string | null {
  const ambiguous = listed.some(
    (other) =>
      other.id !== workArea.id &&
      listedNameKey(other.name) === listedNameKey(workArea.name) &&
      namespaceIdentity(other) !== namespaceIdentity(workArea),
  );
  return ambiguous ? workArea.namespaceName?.trim() || workArea.namespace?.trim() || null : null;
}

/** What a list calls a work area: "Namespace / Work area" where the name is shared, the name alone where not. */
export function workAreaNameAmong(
  workArea: IWorkAreaListedSubject,
  listed: readonly IWorkAreaListedSubject[],
): string {
  const namespace = workAreaNamespaceAmong(workArea, listed);
  return namespace ? `${namespace} / ${workArea.name}` : workArea.name;
}
